/**
 * Where a small panel opens beside the control that opened it, kept on screen.
 *
 * Below the control, unless it would run past the bottom and there is room above, as the
 * Gantt's warning panel did from a low row, 117 px off screen (G-10). With room on neither
 * side it is pinned inside the bottom edge rather than cut off. Sideways it starts a little
 * left of the control and never leaves the screen.
 *
 * @param {{ top: number, bottom: number, left: number }} anchor  the control's client rect
 * @param {{ width: number, height: number }} size  the panel's
 * @param {{ width: number, height: number }} viewport
 * @returns {{ top: number, left: number, above: boolean }}
 */
export function placePopover(anchor, size, viewport, margin = 8, gap = 4) {
    const left = Math.max(margin, Math.min(anchor.left - 8, viewport.width - size.width - margin))
    const below = anchor.bottom + gap
    if (below + size.height <= viewport.height - margin) return { top: below, left, above: false }
    const above = anchor.top - gap - size.height
    if (above >= margin) return { top: above, left, above: true }
    return { top: Math.max(margin, viewport.height - margin - size.height), left, above: false }
}

/**
 * Where a card opens beside an icon in a column of icons: to its LEFT, level with its row, or to
 * its right when there is no room (Kenneth, 29 Sep, the release rows' hover cards). Opened below, a
 * card covered the next rows' icons, so moving down the column landed on the card and kept it open
 * rather than reaching the next icon. Kept inside the top and bottom of the screen.
 *
 * @param {{ top: number, left: number, right: number }} anchor  the icon's client rect
 * @param {{ width: number, height: number }} size  the card's
 * @param {{ width: number, height: number }} viewport
 * @returns {{ top: number, left: number, side: 'left'|'right' }}
 */
export function placeBeside(anchor, size, viewport, margin = 8, gap = 6) {
    const top = Math.max(margin, Math.min(anchor.top, viewport.height - margin - size.height))
    const leftOf = anchor.left - gap - size.width
    if (leftOf >= margin) return { top, left: leftOf, side: 'left' }

    return { top, left: Math.min(anchor.right + gap, viewport.width - margin - size.width), side: 'right' }
}

/**
 * Where a dropdown's list opens, before its height is known (SelectMenu).
 *
 * Below the field, unless there is little room there and more above. Opening above, it is
 * anchored by its BOTTOM edge to the top of the field, so it sits on the field whatever its
 * height. It used to be given a top as if every list were its full 320 px: a short one, such
 * as the bulk drawer's empty Sprint list, then floated 300 px above its own field, over
 * another one (Kenneth, 28 Sep).
 *
 * @param {{ top: number, bottom: number }} anchor  the field's client rect
 * @param {number} viewportHeight
 * @param {number} cap  the tallest the list may be; Infinity for no cap
 * @returns {{ top?: number, bottom?: number, maxHeight: number, above: boolean }}
 */
export function placeMenu(anchor, viewportHeight, cap = 320, margin = 8, gap = 4) {
    const below = viewportHeight - anchor.bottom - margin
    const above = anchor.top - margin
    if (below < 200 && above > below) {
        return { bottom: viewportHeight - anchor.top + gap, maxHeight: Math.min(cap, above), above: true }
    }

    return { top: anchor.bottom + gap, maxHeight: Math.min(cap, below), above: false }
}
